import React, { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion";
import {
  Check, Sparkles, ArrowRight, ArrowUpRight, ChevronDown, ChevronUp, Minus, Calculator,
} from "lucide-react";
import SiteLayout from "@/components/site/SiteLayout";
import { SID } from "@/constants/siteIds";
import { trackEvent, GA_EVENTS } from "@/lib/analytics";
import { PRICING_PRODUCTS } from "@/constants/pricingCatalog";

// Lookup the active tier for a given credit count
function activeTierFor(credits, tiers) {
  return tiers.find((t) => credits >= t.min && (t.max == null || credits <= t.max)) || tiers[0];
}

// Compute the per-credit price after volume discount
function perCreditPrice(tier, base) {
  return base * (1 - (tier.discountPct || 0) / 100);
}

function CoVerifyCalculator({ product, onCta }) {
  const cfg = product.calculator;
  const [credits, setCredits] = useState(cfg.defaultCredits);
  const safe = Math.max(cfg.minCredits, Math.floor(Number(credits) || cfg.minCredits));
  const activeTier = activeTierFor(safe, cfg.tiers);
  const perCredit = perCreditPrice(activeTier, cfg.basePricePerCredit);
  const totalCost = perCredit * safe;
  const baseCost = cfg.basePricePerCredit * safe;
  const savings = baseCost - totalCost;
  const isCustomTier = !!activeTier.priceLabel;

  return (
    <div data-testid="coverify-calculator" className="mb-10">
      {/* Calculator card */}
      <div className="rounded-2xl border border-border bg-white p-6 md:p-8">
        <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.14em] text-primary">
          <Calculator className="h-4 w-4 text-accent" /> Volume calculator
        </div>
        <h3 className="mt-2 font-display text-2xl md:text-3xl font-bold text-primary tracking-tight">
          How many tokens do you need?
        </h3>
        <p className="mt-2 text-sm text-muted-foreground leading-relaxed max-w-2xl">
          Minimum 5 tokens. Base price £{cfg.basePricePerCredit.toFixed(2)} / token. Volume discounts apply automatically, 10% off at 100+ tokens, custom pricing at 500+.
        </p>

        <div className="mt-6 grid md:grid-cols-12 gap-6 items-start">
          <div className="md:col-span-7">
            <label htmlFor="cv-credits" className="text-xs font-bold uppercase tracking-[0.14em] text-muted-foreground">
              Number of tokens
            </label>
            <div className="mt-2 flex items-stretch rounded-xl border border-border overflow-hidden focus-within:border-accent transition-colors">
              <button
                type="button"
                data-testid="coverify-calc-decr"
                onClick={() => setCredits((c) => Math.max(cfg.minCredits, Math.floor(Number(c) || cfg.minCredits) - 5))}
                className="px-4 text-primary hover:bg-secondary font-bold text-lg"
                aria-label="decrease"
              >
                −
              </button>
              <input
                id="cv-credits"
                data-testid="coverify-calc-input"
                type="number"
                min={cfg.minCredits}
                step={1}
                value={credits}
                onChange={(e) => setCredits(e.target.value)}
                onBlur={() => setCredits(safe)}
                className="flex-1 text-center text-2xl font-display font-black text-primary px-3 py-3 outline-none"
              />
              <button
                type="button"
                data-testid="coverify-calc-incr"
                onClick={() => setCredits((c) => Math.floor(Number(c) || cfg.minCredits) + 5)}
                className="px-4 text-primary hover:bg-secondary font-bold text-lg"
                aria-label="increase"
              >
                +
              </button>
            </div>
            <input
              type="range"
              min={cfg.minCredits}
              max={1000}
              step={5}
              value={Math.min(safe, 1000)}
              onChange={(e) => setCredits(parseInt(e.target.value, 10))}
              className="mt-4 w-full accent-accent"
              aria-label="tokens slider"
              data-testid="coverify-calc-slider"
            />
            <div className="mt-1 flex justify-between text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
              <span>5</span><span>100</span><span>500</span><span>1,000+</span>
            </div>
          </div>

          <div className="md:col-span-5 rounded-xl bg-primary text-primary-foreground p-6">
            <div className="text-[10px] font-bold uppercase tracking-[0.14em] text-accent">Your total</div>
            <div className="mt-1 font-display text-4xl font-black tracking-tight" data-testid="coverify-calc-total">
              {isCustomTier ? activeTier.priceLabel : `£${totalCost.toFixed(2)}`}
            </div>
            <div className="mt-1 text-xs text-white/70" data-testid="coverify-calc-perunit">
              {isCustomTier
                ? `${safe.toLocaleString()} tokens · custom enterprise pricing`
                : `${safe.toLocaleString()} tokens · £${perCredit.toFixed(3).replace(/0$/, "")} / token`}
            </div>
            {!isCustomTier && savings > 0 && (
              <div className="mt-3 inline-flex items-center gap-1.5 rounded-full bg-accent/20 text-accent text-[10px] font-bold uppercase tracking-wider px-2 py-1">
                <Sparkles className="h-3 w-3" /> You save £{savings.toFixed(2)}
              </div>
            )}
            <div className="mt-4 inline-flex items-center gap-1.5 text-xs font-bold text-accent">
              Active tier: {activeTier.name}
            </div>
            <Button
              asChild
              data-testid="coverify-calc-cta"
              onClick={() => onCta && onCta(activeTier, safe, totalCost)}
              className="mt-5 w-full h-11 bg-accent hover:bg-accent/90 text-primary font-bold"
            >
              <Link to={activeTier.href}>{activeTier.cta} <ArrowRight className="ml-1 h-4 w-4" /></Link>
            </Button>
          </div>
        </div>
      </div>

      {/* Tier cards */}
      <div data-testid="pricing-plan-grid" className="mt-6 grid md:grid-cols-3 gap-5">
        {cfg.tiers.map((tier) => {
          const isActive = tier.key === activeTier.key;
          const tierIsCustom = !!tier.priceLabel;
          const tierPerCredit = perCreditPrice(tier, cfg.basePricePerCredit);
          return (
            <div
              key={tier.key}
              data-testid={`pricing-plan-coverify-${tier.key}`}
              className={`relative rounded-2xl p-6 flex flex-col transition-all ${
                isActive
                  ? "bg-primary text-primary-foreground border-2 border-accent shadow-[0_24px_60px_rgba(14,58,95,0.18)]"
                  : "bg-white border border-border"
              }`}
            >
              {tier.badge && (
                <span className="absolute -top-3 left-6 inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-[0.14em] rounded-full bg-accent text-primary px-2.5 py-1">
                  <Sparkles className="h-3 w-3" /> {tier.badge}
                </span>
              )}
              {isActive && (
                <span className="absolute top-4 right-4 inline-flex items-center gap-1 rounded-full bg-accent/25 text-accent text-[9px] font-bold uppercase tracking-wider px-2 py-0.5">
                  Current
                </span>
              )}
              <div className={`font-display text-lg font-bold tracking-tight ${isActive ? "" : "text-primary"}`}>
                {tier.name}
              </div>
              <p className={`mt-1 text-sm leading-relaxed ${isActive ? "text-white/75" : "text-muted-foreground"}`}>
                {tier.tagline}
              </p>
              <div className="mt-5">
                <div className={`font-display text-4xl font-black tracking-tight ${isActive ? "" : "text-primary"}`}>
                  {tierIsCustom ? tier.priceLabel : `£${tierPerCredit.toFixed(3).replace(/0$/, "")}`}
                </div>
                <div className={`mt-1 text-xs ${isActive ? "text-white/70" : "text-muted-foreground"}`}>
                  {tierIsCustom
                    ? "Custom pricing · 500+ tokens"
                    : `/ token · ${tier.max ? `${tier.min}–${tier.max} tokens` : `${tier.min}+ tokens`}`}
                </div>
              </div>
              <ul className="mt-5 space-y-2.5 flex-1">
                {tier.includes.map((it) => (
                  <li key={it} className="flex items-start gap-2 text-sm leading-snug">
                    <Check className="h-4 w-4 mt-0.5 shrink-0 text-accent" />
                    <span className={isActive ? "text-white/90" : "text-primary/90"}>{it}</span>
                  </li>
                ))}
              </ul>
              <Button
                asChild
                data-testid={`pricing-cta-coverify-${tier.key}`}
                onClick={() => { try { trackEvent("pricing_cta_click", { product: "coverify", plan: tier.key, billing: "tokens" }); } catch (e) { /* noop */ } }}
                className={`mt-6 h-11 font-bold ${
                  isActive
                    ? "bg-accent hover:bg-accent/90 text-primary"
                    : "bg-primary hover:bg-primary/90 text-primary-foreground"
                }`}
              >
                <Link to={tier.href}>{tier.cta} <ArrowRight className="ml-1 h-4 w-4" /></Link>
              </Button>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// Compute the displayed price for a plan given the current annual/monthly toggle.
function priceFor(plan, billing, product) {
  if (plan.priceLabel) return { label: plan.priceLabel, sub: plan.priceSub };
  // CoSec App uses annual list price + a monthly uplift
  if (product.key === "cosec-app") {
    const base = plan.annual;
    if (billing === "annual") {
      return { label: `£${base}`, sub: `/ ${product.period} · ${product.periodNote}` };
    }
    const monthly = Math.round(base * (1 + (product.monthlyMarkupPct || 20) / 100));
    return { label: `£${monthly}`, sub: `/ ${product.period} · billed monthly` };
  }
  // CoSearch, distinct monthly vs annual price points
  if (product.key === "cosearch") {
    const value = billing === "annual" ? plan.annual : plan.monthly;
    const sub = billing === "annual" ? `/ ${product.period} · billed annually` : `/ ${product.period} · cancel anytime`;
    return { label: `£${value}`, sub };
  }
  // Fallback (shouldn't hit), use annual
  return { label: `£${plan.annual}`, sub: `/ ${product.period}` };
}

function FeatureCell({ v }) {
  if (v === true) return <Check className="h-4 w-4 text-accent inline-block" aria-label="included" />;
  if (v === false) return <Minus className="h-4 w-4 text-muted-foreground/40 inline-block" aria-label="not included" />;
  return <span className="text-xs font-semibold text-primary">{v}</span>;
}

export default function PricingPage() {
  const [activeKey, setActiveKey] = useState("cosec-app");
  const [billing, setBilling] = useState("annual");
  const [showCompare, setShowCompare] = useState(false);

  useEffect(() => {
    try { trackEvent(GA_EVENTS.PRICING_VIEW, { source: "page_load" }); } catch (e) { /* noop */ }
  }, []);

  useEffect(() => {
    try { trackEvent("pricing_tab_view", { product: activeKey }); } catch (e) { /* noop */ }
  }, [activeKey]);

  const product = useMemo(
    () => PRICING_PRODUCTS.find((p) => p.key === activeKey) || PRICING_PRODUCTS[0],
    [activeKey]
  );

  // Some products (CoForm) have flat pricing, no period toggle. CoVerify uses calculator instead.
  const showBillingToggle = product.key === "cosec-app" || product.key === "cosearch";
  const isCalculator = !!product.calculator;

  return (
    <SiteLayout>
      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-dotgrid opacity-50 pointer-events-none" />
        <div className="absolute -top-32 -right-32 h-96 w-96 rounded-full bg-accent/10 blur-3xl pointer-events-none" />
        <div className="container-cv relative pt-16 md:pt-24 pb-10 max-w-5xl">
          <span className="pill"><Sparkles className="h-3.5 w-3.5 text-accent" /> Pricing</span>
          <h1 className="mt-6 font-display text-4xl sm:text-5xl lg:text-6xl font-bold text-primary leading-[1.04] tracking-tight">
            One suite. {" "}
            <span className="relative inline-block">
              <span className="relative z-10 text-accent">Four pricing models.</span>
              <span className="absolute left-0 bottom-1 h-3 w-full bg-accent/30 -z-0 rounded-sm" />
            </span>
          </h1>
          <p className="mt-5 text-base md:text-lg text-muted-foreground leading-relaxed max-w-3xl">
            Pick a product to see its plans. Subscriptions are no-commitment, cancel any time with 30 days&apos; notice. CoVerify is sold as add-on credits that work across the entire suite.
          </p>
        </div>
      </section>

      {/* Product tabs */}
      <section className="border-b border-border bg-white sticky top-16 md:top-20 z-30">
        <div className="container-cv">
          <div role="tablist" aria-label="Product pricing" className="flex flex-nowrap whitespace-nowrap gap-1 py-3 overflow-x-auto -mx-4 px-4 md:mx-0 md:px-0">
            {PRICING_PRODUCTS.map((p) => {
              const active = p.key === activeKey;
              return (
                <button
                  key={p.key}
                  role="tab"
                  aria-selected={active}
                  data-testid={`pricing-tab-${p.key}`}
                  onClick={() => { setActiveKey(p.key); setShowCompare(false); }}
                  className={`relative px-4 md:px-5 py-2.5 rounded-full text-sm font-bold tracking-tight transition-colors ${
                    active
                      ? "bg-primary text-primary-foreground"
                      : "text-primary/70 hover:text-primary hover:bg-secondary"
                  }`}
                >
                  {p.label}
                  {p.key === "coverify" && (
                    <span className={`ml-2 text-[9px] font-bold uppercase tracking-wider rounded-full px-1.5 py-0.5 ${active ? "bg-accent/30 text-primary-foreground" : "bg-accent/25 text-primary"}`}>
                      Add-on
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      </section>

      {/* Plans */}
      <section className="bg-secondary/30 border-b border-border">
        <div className="container-cv py-14 md:py-20">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-10">
            <div className="max-w-2xl">
              <h2 className="font-display text-2xl md:text-4xl font-bold text-primary tracking-tight leading-tight">
                {product.label} <span className="text-accent">pricing</span>
              </h2>
              <p className="mt-2 text-sm md:text-base text-muted-foreground leading-relaxed">{product.blurb}</p>
            </div>
            {showBillingToggle && (
              <div className="inline-flex items-center gap-1 rounded-full border border-border bg-white p-1 self-start md:self-auto" role="radiogroup" aria-label="Billing period">
                {["monthly", "annual"].map((opt) => {
                  const isOn = billing === opt;
                  return (
                    <button
                      key={opt}
                      role="radio"
                      aria-checked={isOn}
                      data-testid={`pricing-billing-${opt}`}
                      onClick={() => setBilling(opt)}
                      className={`px-4 py-2 text-xs font-bold uppercase tracking-wider rounded-full transition-colors ${
                        isOn ? "bg-primary text-primary-foreground" : "text-primary/70 hover:text-primary"
                      }`}
                    >
                      {opt === "annual" ? "Annual · save 20%" : "Monthly"}
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          <div
            data-testid={isCalculator ? "pricing-plan-grid-calculator-wrap" : "pricing-plan-grid"}
            className={isCalculator ? "" : `grid gap-5 ${
              product.plans.length === 2 ? "md:grid-cols-2" :
              product.plans.length === 3 ? "md:grid-cols-3" :
              product.plans.length === 4 ? "md:grid-cols-2 lg:grid-cols-4" :
              "md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5"
            }`}
          >
            {isCalculator ? (
              <CoVerifyCalculator product={product} />
            ) : product.plans.map((plan) => {
              const { label, sub } = priceFor(plan, billing, product);
              const featured = !!plan.featured;
              return (
                <div
                  key={plan.key}
                  data-testid={`pricing-plan-${product.key}-${plan.key}`}
                  className={`relative rounded-2xl p-6 flex flex-col ${
                    featured
                      ? "bg-primary text-primary-foreground border-2 border-accent shadow-[0_24px_60px_rgba(14,58,95,0.18)]"
                      : "bg-white border border-border"
                  }`}
                >
                  {plan.badge && (
                    <span className="absolute -top-3 left-6 inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-[0.14em] rounded-full bg-accent text-primary px-2.5 py-1">
                      <Sparkles className="h-3 w-3" /> {plan.badge}
                    </span>
                  )}
                  <div className={`font-display text-lg font-bold tracking-tight ${featured ? "" : "text-primary"}`}>
                    {plan.name}
                  </div>
                  <p className={`mt-1 text-sm leading-relaxed ${featured ? "text-white/75" : "text-muted-foreground"}`}>
                    {plan.tagline}
                  </p>
                  <div className="mt-5">
                    <div className={`font-display text-4xl font-black tracking-tight ${featured ? "" : "text-primary"}`}>
                      {label}
                    </div>
                    <div className={`mt-1 text-xs ${featured ? "text-white/70" : "text-muted-foreground"}`}>
                      {sub}
                    </div>
                  </div>
                  <ul className="mt-5 space-y-2.5 flex-1">
                    {plan.includes.map((it) => (
                      <li key={it} className="flex items-start gap-2 text-sm leading-snug">
                        <Check className={`h-4 w-4 mt-0.5 shrink-0 ${featured ? "text-accent" : "text-accent"}`} />
                        <span className={featured ? "text-white/90" : "text-primary/90"}>{it}</span>
                      </li>
                    ))}
                  </ul>
                  <Button
                    asChild
                    data-testid={`pricing-cta-${product.key}-${plan.key}`}
                    onClick={() => { try { trackEvent("pricing_cta_click", { product: product.key, plan: plan.key, billing }); } catch (e) { /* noop */ } }}
                    className={`mt-6 h-11 font-bold ${
                      featured
                        ? "bg-accent hover:bg-accent/90 text-primary"
                        : "bg-primary hover:bg-primary/90 text-primary-foreground"
                    }`}
                  >
                    <Link to={plan.href}>{plan.cta} <ArrowRight className="ml-1 h-4 w-4" /></Link>
                  </Button>
                </div>
              );
            })}
          </div>

          {/* Compare features (Athennian-style toggle) */}
          {product.matrix && product.matrix.length > 0 && (
            <div className="mt-12 text-center">
              <button
                data-testid="pricing-compare-toggle"
                onClick={() => setShowCompare((v) => !v)}
                className="inline-flex items-center gap-2 rounded-full border border-primary/15 bg-white text-primary text-sm font-bold px-5 py-2.5 hover:bg-primary hover:text-primary-foreground transition-colors"
              >
                {showCompare ? <>Hide feature comparison <ChevronUp className="h-4 w-4" /></> : <>Compare all features <ChevronDown className="h-4 w-4" /></>}
              </button>
            </div>
          )}
        </div>
      </section>

      {/* Feature matrix */}
      {showCompare && product.matrix && (
        <section data-testid="pricing-compare-matrix" className="border-b border-border bg-white">
          <div className="container-cv py-12">
            {/* Sticky header row of plans */}
            <div className="overflow-x-auto rounded-2xl border border-border">
              <table className="w-full text-sm min-w-[640px]">
                <thead className="bg-white sticky top-32 z-10 shadow-[0_1px_0_rgba(14,58,95,0.08)]">
                  <tr>
                    <th className="text-left p-4 font-display font-bold text-primary tracking-tight w-1/3">Features</th>
                    {(product.calculator ? product.calculator.tiers : product.plans).map((p) => (
                      <th key={p.key} className="p-4 text-center">
                        <div className="font-display font-bold text-primary tracking-tight">{p.name}</div>
                        {p.featured && (
                          <span className="mt-1 inline-block text-[9px] font-bold uppercase tracking-wider rounded-full bg-accent/25 text-primary px-1.5 py-0.5">
                            {p.badge || "Popular"}
                          </span>
                        )}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {product.matrix.map((group, gi) => (
                    <React.Fragment key={group.group}>
                      <tr className={gi === 0 ? "" : "border-t-8 border-white"}>
                        <td colSpan={(product.calculator ? product.calculator.tiers.length : product.plans.length) + 1} className="bg-primary/5 px-4 py-3 font-display text-xs font-bold uppercase tracking-[0.14em] text-primary">
                          {group.group}
                        </td>
                      </tr>
                      {group.rows.map((row) => (
                        <tr key={row.name} className="border-t border-border/60">
                          <td className="p-4 text-primary/90">{row.name}</td>
                          {row.values.map((v, i) => (
                            <td key={i} className="p-4 text-center">
                              <FeatureCell v={v} />
                            </td>
                          ))}
                        </tr>
                      ))}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="mt-6 text-xs text-muted-foreground text-center">
              All plans include unlimited users and 24/7 in-app support. Subscriptions are no-commitment, billed monthly or annually.
            </div>
          </div>
        </section>
      )}

      {/* FAQs */}
      <section className="container-cv py-14 md:py-20 max-w-3xl">
        <span className="section-eyebrow">FAQs</span>
        <h2 className="mt-3 font-display text-3xl md:text-4xl font-bold text-primary tracking-tight leading-tight">
          Pricing & subscriptions, <span className="text-accent">explained.</span>
        </h2>
        <Accordion type="single" collapsible className="mt-8" data-testid="pricing-faqs">
          <AccordionItem value="q1">
            <AccordionTrigger>Can I change plans later?</AccordionTrigger>
            <AccordionContent className="text-sm text-muted-foreground leading-relaxed">
              Yes. Upgrade or downgrade at any time from inside CoSec App. Changes apply from the next billing cycle.
            </AccordionContent>
          </AccordionItem>
          <AccordionItem value="q2">
            <AccordionTrigger>Are there long-term contracts?</AccordionTrigger>
            <AccordionContent className="text-sm text-muted-foreground leading-relaxed">
              No. Subscriptions run on a rolling monthly or annual basis. Cancel by giving 30 days&apos; notice before the next renewal date.
            </AccordionContent>
          </AccordionItem>
          <AccordionItem value="q3">
            <AccordionTrigger>Do CoVerify credits expire?</AccordionTrigger>
            <AccordionContent className="text-sm text-muted-foreground leading-relaxed">
              Credit packs (100, 500) are valid for 12 months from purchase. Enterprise contracts can negotiate longer windows.
            </AccordionContent>
          </AccordionItem>
          <AccordionItem value="q4">
            <AccordionTrigger>What does the £100 Companies House fee on CoForm cover?</AccordionTrigger>
            <AccordionContent className="text-sm text-muted-foreground leading-relaxed">
              That&apos;s the statutory fee Companies House charges to incorporate a UK limited company digitally, effective 1 February 2026. It&apos;s a one-off cost paid to the government, not to us. We display it separately so you can see exactly what you&apos;re paying.
            </AccordionContent>
          </AccordionItem>
          <AccordionItem value="q5">
            <AccordionTrigger>Do you offer custom or enterprise plans?</AccordionTrigger>
            <AccordionContent className="text-sm text-muted-foreground leading-relaxed">
              Yes, for portfolios above 300 entities, high-volume KYC, multi-seat CoSearch, or co-branded CoForm flows. Contact sales below.
            </AccordionContent>
          </AccordionItem>
        </Accordion>
      </section>

      {/* Final CTA. Book a demo + Contact sales */}
      <section className="container-cv pb-20 md:pb-28">
        <div className="rounded-3xl border border-border bg-primary text-primary-foreground p-10 md:p-14 relative overflow-hidden">
          <div className="absolute inset-0 bg-dotgrid opacity-[0.08] pointer-events-none" />
          <div className="relative grid md:grid-cols-12 gap-8 items-center">
            <div className="md:col-span-7">
              <h2 className="font-display text-3xl md:text-4xl font-bold leading-tight">
                Not sure which plan fits?{" "}
                <span className="text-accent">We&apos;ll help you pick.</span>
              </h2>
              <p className="mt-3 text-white/80 leading-relaxed max-w-xl">
                Book a 30-minute demo and we&apos;ll show you the suite in action with your own entities, or speak to sales for custom pricing.
              </p>
            </div>
            <div className="md:col-span-5 flex flex-col sm:flex-row md:flex-col gap-3 md:items-end">
              <Button asChild data-testid="pricing-bottom-book-demo" className="h-12 px-6 bg-accent hover:bg-accent/90 text-primary font-bold w-full sm:w-auto">
                <Link to="/contact" onClick={() => { try { trackEvent(GA_EVENTS.BOOK_DEMO_CLICK, { source: "pricing_bottom" }); } catch (e) { /* noop */ } }}>
                  Book a Demo <ArrowUpRight className="ml-1 h-4 w-4" />
                </Link>
              </Button>
              <Button asChild variant="outline" data-testid="pricing-bottom-contact-sales" className="h-12 px-6 border-white/30 text-white hover:bg-white hover:text-primary font-bold w-full sm:w-auto">
                <Link to="/contact">Contact sales <ArrowRight className="ml-1 h-4 w-4" /></Link>
              </Button>
            </div>
          </div>
        </div>
      </section>
    </SiteLayout>
  );
}
